'use client';

import classNames from 'classnames';
import { Suspense, useCallback, useMemo } from 'react';
import DynamicTrailListMap, { LoadingMap } from '@alltrails/maps/components/DynamicTrailListMap/DynamicTrailListMap';
import { updateActiveTrails } from '@alltrails/maps/redux/reducer';
import { convertTrailToTrailListItemData, convertTrailToTrailSearchResult, TrailList } from '@alltrails/trails';
import type Trail from '@alltrails/types/Trail';
import { LandingPageVariant, TrailListBlock, TrailListItemData } from '@/types/shared';
import { useAppDispatch } from '@/utils/redux';
import styles from './PartnerListTrailList.module.scss';

type Props = {
  trailListBlock: TrailListBlock;
  className?: string;
};

type PartnerListTrailListWithMapProps = {
  trails: Trail[];
  trailListTrails: TrailListItemData[];
  hideTrailNumber?: boolean;
};

const PartnerListTrailListWithMap = ({ trails, trailListTrails, hideTrailNumber }: PartnerListTrailListWithMapProps) => {
  const dispatch = useAppDispatch();

  const algoliaFormattedTrails = useMemo(() => {
    return trails.map((trail, index) => ({
      ...convertTrailToTrailSearchResult(trail),
      // Popularity is used in `symbol-sort-key` to avoid text overlap between trail pins
      popularity: trails.length - index
    }));
  }, [trails]);

  const onListItemHover = useCallback(
    (index?: number) => {
      if (index === undefined || !algoliaFormattedTrails) {
        dispatch(updateActiveTrails(undefined));
      } else {
        const trail = algoliaFormattedTrails[index];
        if (trail && trail._geoloc) {
          dispatch(updateActiveTrails([trail]));
        }
      }
    },
    [dispatch, algoliaFormattedTrails]
  );

  return (
    <>
      <div className={styles.listContainer}>
        <TrailList
          trails={trailListTrails}
          variant={LandingPageVariant.Inverse}
          setIndexHovered={onListItemHover}
          hideTrailNumber={hideTrailNumber}
        />
      </div>
      {algoliaFormattedTrails && (
        <div className={styles.mapContainer}>
          <div className={styles.map}>
            <Suspense fallback={<LoadingMap />}>
              <DynamicTrailListMap isLoading={false} trailSearchResults={algoliaFormattedTrails} />
            </Suspense>
          </div>
        </div>
      )}
    </>
  );
};

const PartnerListTrailList = ({ trailListBlock, className }: Props) => {
  const { show_map: showMap, trails } = trailListBlock;

  const hideTrailNumber = trails.length === 1;
  const trailListTrails = useMemo(() => {
    return trails.map(trail => convertTrailToTrailListItemData(trail));
  }, [trails]);

  if (showMap) {
    return (
      <div className={classNames(className, styles.mapAndListContainer)}>
        <PartnerListTrailListWithMap trails={trails} trailListTrails={trailListTrails} hideTrailNumber={hideTrailNumber} />
      </div>
    );
  }

  return (
    <div className={classNames(className, styles.mapAndListContainer)}>
      <TrailList trails={trailListTrails} variant={LandingPageVariant.Inverse} hideTrailNumber={hideTrailNumber} />
    </div>
  );
};

export default PartnerListTrailList;
